import { useQuery } from "@tanstack/react-query";
import type { Table } from "@tanstack/react-table";
import React from "react";
import useApi from "../../../../shared/hooks/useApi";
import {
  contributeAiCapabilityParamOption,
  declareAiCapability,
  useProvideAiCapability,
} from "../../../ai-omnibar/ai-capability-registry";

/**
 * What the caregivers list can do for the AI omnibar.
 *
 * `scope: "page"` because these only work while this page is on screen - `app.navigate` declares
 * that it opens that space, so "active caregivers in Brooklyn" comes back as navigate + filter
 * rather than navigate and nothing.
 */

const CAREGIVER_STATUSES = [
  "ACTIVE",
  "JOIN_REQUEST",
  "ON_HOLD",
  "ON_LEAVE",
  "PENDING",
  "QUIT",
  "REJECTED",
  "SUSPENDED",
  "TERMINATED",
] as const;

/** The page's own Installation control, which is a fixed list rather than agency data. */
const INSTALLATION_OPTIONS = [
  { value: "installed", label: "Installed" },
  { value: "uninstalled", label: "Un-Installed" },
  { value: "notinstalled", label: "Not Installed" },
];

/**
 * An option value back in the type the query wants.
 *
 * Everything crosses the omnibar's wire as a string, but the filter variables are typed - officeId is
 * `[OfficeId!]`, and a "2" where a 2 belongs makes the GraphQL call fail with a 500, which takes the
 * whole route down with it rather than showing an error.
 */
function toIdValue(value: string): string | number {
  return /^\d+$/.test(value) ? Number(value) : value;
}

function toStatusLabel(status: string): string {
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

declareAiCapability({
  id: "caregivers.filter",
  title: "Filter the caregivers list",
  effect: "prefill",
  description:
    'Sets the filters on the caregivers list - search, status, office, certification, language, whether the app is installed, and the hire date range - so the table shows the caregivers being asked about. Only the newer list has these filters: it is the page called "Caregivers list (new)" in the navigation, so open that one rather than the older caregivers list.',
  location: "the filter bar of the Caregivers list (new) page",
  scope: "page",
  permissionKey: "view_caregivers_section_list",
  params: [
    {
      name: "search",
      type: "text",
      required: false,
      description:
        "Free text over the table, for what no other filter can hold: a person's name, a caregiver id, a street, a phone number. An office, certification or language belongs in its own filter, not here.",
      // Where a word that is in none of this screen's lists ends up, rather than the step failing.
      searchesFreeText: true,
    },
    {
      name: "status",
      type: "enum",
      required: false,
      description: "Show only caregivers with this status.",
      options: CAREGIVER_STATUSES.map((status) => ({
        value: status,
        label: toStatusLabel(status),
      })),
    },
    {
      name: "installation",
      type: "enum",
      required: false,
      description: "Whether the caregiver has installed the mobile app.",
      options: INSTALLATION_OPTIONS,
    },
    // Agency data the page already loads, contributed by the hook below rather than declared here.
    {
      name: "office",
      type: "enum",
      required: false,
      description: "Show only caregivers of one office.",
    },
    {
      name: "certification",
      type: "enum",
      required: false,
      description: "Show only caregivers holding one certification, e.g. HHA or PCA.",
    },
    {
      name: "language",
      type: "enum",
      required: false,
      description: "Show only caregivers who speak one language.",
    },
    {
      name: "hiredFrom",
      type: "date",
      required: false,
      description: "Earliest hire date to include, as yyyy-MM-dd.",
    },
    {
      name: "hiredTo",
      type: "date",
      required: false,
      description: "Latest hire date to include, as yyyy-MM-dd.",
    },
  ],
  examples: [
    "show me active caregivers",
    "HHA caregivers in the Brooklyn office",
    "caregivers who speak russian",
  ],
});

/**
 * Publishes an `{ value, label }` list as a param's options for as long as this page is mounted.
 *
 * Contributed rather than declared because these are agency records the page loads: they do not
 * exist when the module is imported, and they should leave the manifest with the page.
 */
function useContributedOptions(
  paramName: string,
  options: readonly { value: string; label: string }[] | undefined
): void {
  const key = (options ?? []).map((option) => `${option.value}:${option.label}`).join("|");

  React.useEffect(() => {
    const releases = (options ?? []).map((option) =>
      contributeAiCapabilityParamOption("caregivers.filter", paramName, option)
    );

    return () => releases.forEach((release) => release());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramName, key]);
}

const LANGUAGES = [
  "English",
  "Spanish",
  "Chinese",
  "Portuguese",
  "French",
  "Russian",
  "Arabic",
] as const;

declareAiCapability({
  id: "caregivers.columns",
  title: "Show or hide columns on the caregivers list",
  effect: "prefill",
  description:
    "Turns columns of the caregivers list on or off. The choice sticks for this user, the same as using the Columns control.",
  location: "the Columns control of the caregivers list",
  scope: "page",
  permissionKey: "view_caregivers_section_list",
  params: [
    {
      name: "field",
      type: "enum",
      required: true,
      multiple: true,
      description:
        "Which columns to change. Several at once are a selection rather than a conflict: asking for the certifications and address columns turns both on in one step.",
      // Contributed by the mounted table, because the column set is the table's own business.
    },
    {
      name: "show",
      type: "boolean",
      required: false,
      description: "True to show the columns, false to hide them. Defaults to showing them.",
    },
  ],
  examples: ["show the address column", "hide the phone numbers column"],
});

/** The label the Columns control shows for a column, falling back to its id. */
function getCaregiverColumnLabel(column: { id: string; columnDef: { header?: unknown } }): string {
  const header = column.columnDef.header;

  return typeof header === "string" && header !== "" ? header : column.id;
}

export function useCaregiversDashboardAiCapabilities<TData>(params: {
  table: Table<TData>;
  /**
   * The page's own setFilter. Typed loosely on purpose: it is generic over that page's query
   * variables, and this hook only ever passes the keys it declares.
   */
  setFilter: (key: string, value: unknown) => void;
}): void {
  const { table, setFilter } = params;
  const { queries } = useApi();

  const setFilterRef = React.useRef(setFilter);
  setFilterRef.current = setFilter;

  const tableRef = React.useRef(table);
  tableRef.current = table;

  // Re-contributed when the column set itself changes, which happens when custom fields arrive.
  const columns = table.getAllLeafColumns().filter((column) => column.getCanHide());
  const columnsKey = columns.map((column) => column.id).join("|");

  React.useEffect(() => {
    const releases = tableRef.current
      .getAllLeafColumns()
      .filter((column) => column.getCanHide())
      .map((column) =>
        contributeAiCapabilityParamOption("caregivers.columns", "field", {
          value: column.id,
          label: getCaregiverColumnLabel(column),
        })
      );

    return () => releases.forEach((release) => release());
  }, [columnsKey]);

  // The same queries the filter bar's own selects use, so this reads their cache rather than
  // fetching anything of its own.
  const offices = useQuery(queries.office.list());
  const certifications = useQuery(queries.caregiver.certifications());

  useContributedOptions(
    "office",
    React.useMemo(
      () =>
        (offices.data?.offices ?? []).map((office) => ({
          value: String(office.id),
          label: office.name,
        })),
      [offices.data?.offices]
    )
  );

  useContributedOptions(
    "certification",
    React.useMemo(
      () =>
        (certifications.data ?? []).map((row) => ({
          value: row.certification,
          label: row.certification,
        })),
      [certifications.data]
    )
  );

  useContributedOptions(
    "language",
    React.useMemo(() => LANGUAGES.map((language) => ({ value: language, label: language })), [])
  );

  // Not available until the filter bar's own lists have arrived - see the same note on the patients
  // list. An empty option list turns a real filter into a free-text search.
  const listsLoaded = offices.isSuccess && certifications.isSuccess;

  useProvideAiCapability(
    "caregivers.filter",
    (args, context) => {
      const applied: string[] = [];
      // The label the option carried, so the line says "Brooklyn" rather than an id.
      const label = (name: string) => context.labels[name] ?? String(args[name]);

      // Every filter this page holds as a list of one, plus the plain values. "id" is a list whose
      // entries are typed ids rather than free strings.
      for (const [name, key, shape] of [
        ["search", "search", "plain"],
        ["status", "status", "list"],
        ["office", "officeId", "id"],
        ["certification", "certifications", "list"],
        ["language", "languages", "list"],
        ["installation", "installation", "plain"],
        ["hiredFrom", "fromHireDate", "plain"],
        ["hiredTo", "toHireDate", "plain"],
      ] as const) {
        if (args[name] === undefined) {
          continue;
        }

        const value = String(args[name]);

        setFilterRef.current(
          key,
          shape === "id" ? [toIdValue(value)] : shape === "list" ? [value] : value
        );
        applied.push(name === "search" ? `"${value}"` : label(name));
      }

      if (applied.length === 0) {
        throw new Error("Tell me what to filter the list by.");
      }

      return `Showing caregivers: ${applied.join(", ")}`;
    },
    { enabled: listsLoaded }
  );

  useProvideAiCapability("caregivers.columns", (args) => {
    const wanted = Array.isArray(args.field) ? args.field : [args.field];
    const columns = wanted.map((one) => {
      const column = tableRef.current
        .getAllLeafColumns()
        .find((candidate) => candidate.id === String(one));

      if (column === undefined) {
        throw new Error(`The caregivers list has no "${String(one)}" column.`);
      }

      return column;
    });

    const shouldShow = args.show !== false;

    // Every column is resolved before any is toggled: half a selection applied is worse than none,
    // because the person cannot tell which half.
    columns.forEach((column) => column.toggleVisibility(shouldShow));

    const names = columns.map((column) => getCaregiverColumnLabel(column)).join(" and ");

    return `${shouldShow ? "Showing" : "Hid"} the ${names} column${
      columns.length === 1 ? "" : "s"
    }`;
  });
}
